import { Fragment, HTMLAttributes } from 'react'
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View
} from '@react-pdf/renderer'

import { WPPageData } from '@/types'

Font.register({
  family: 'Inter',
  fonts: [
    { src: '/fonts/Inter-Regular.otf' },
    { src: '/fonts/Inter-Light.otf', fontWeight: 300 },
    { src: '/fonts/Inter-Medium.otf', fontWeight: 500 }
  ]
})

const styles = StyleSheet.create({
  page: {
    paddingTop: '30mm',
    paddingBottom: '25mm',
    paddingHorizontal: '10mm',
    fontFamily: 'Inter'
  },
  header: {
    position: 'absolute',
    top: '10mm',
    left: '10mm',
    right: '10mm',
    height: '15mm',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#f2f2f2'
  },
  footer: {
    position: 'absolute',
    bottom: '10mm',
    left: '10mm',
    right: '10mm',
    height: '10mm',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    fontSize: 8,
    borderTopWidth: 1,
    borderTopColor: '#f2f2f2',
    color: '#000000'
  },
  logo: {
    width: 141,
    height: 8
  },
  location: {
    fontSize: 8,
    textTransform: 'uppercase'
  },
  pageTitle: {
    fontSize: 32,
    fontWeight: 500
  },
  pretitle: {
    fontSize: 6,
    textTransform: 'uppercase',
    marginBottom: 10
  },
  title: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 10
  },
  text: {
    fontSize: 12,
    fontWeight: 300
  },
  image: {
    width: '100%',
    height: 'auto',
    maxHeight: '70vh', // Ridotto da 100vh a 60vh
    objectFit: 'contain',
    marginVertical: 10
  },
  imageContainer: {
    marginBottom: 20,
    pageBreakInside: 'avoid',
    pageBreakBefore: 'auto',
    pageBreakAfter: 'auto'
  },
  galleryImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  galleryImageContainer: {
    width: '100%',
    height: '118mm',
    marginBottom: '3mm',
    pageBreakInside: 'avoid',
    pageBreakBefore: 'auto',
    pageBreakAfter: 'auto'
  }
})

export type ProjectDataSheetProps = HTMLAttributes<HTMLElement> &
  Partial<{
    pageData: WPPageData
  }>

const ProjectDataSheet = ({ pageData }: ProjectDataSheetProps) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <Image src="/images/logo.jpg" style={styles.logo} />
        </View>

        <View>
          <Text style={styles.location}>
            {pageData?.acf.hero.headline ? pageData.acf.hero.headline : ''}
          </Text>
          <Text style={styles.pageTitle}>
            {pageData?.title?.rendered ? pageData.title.rendered : ''}
          </Text>
          {pageData?.acf?.hero?.poster?.desktop?.url ? (
            <View style={styles.imageContainer}>
              <Image
                style={styles.image}
                src={pageData.acf.hero.poster.desktop.url}
              />
            </View>
          ) : null}
        </View>
        {pageData?.acf?.builder?.length &&
          pageData?.acf?.builder?.length > 0 &&
          pageData.acf.builder
            .filter((builder) =>
              [
                // 'spacer',
                'double_paragraph',
                'vertical_image_paragraph',
                'text_on_image',
                'single_img',
                'simple_paragraph',
                'gallery_download'
              ].includes(builder.acf_fc_layout)
            )
            .map((component, index) => {
              if (component.acf_fc_layout === 'spacer') {
                return (
                  <View
                    key={index}
                    style={{
                      height: 30,
                      backgroundColor: 'transparent'
                    }}
                  ></View>
                )
              }
              if (component.acf_fc_layout === 'vertical_image_paragraph') {
                return (
                  <View key={index}>
                    {component.paragraph.pretitle ? (
                      <Text style={styles.pretitle}>
                        {component.paragraph.pretitle}
                      </Text>
                    ) : null}
                    {component.paragraph.title ? (
                      <Text style={styles.title}>
                        {component.paragraph.title}
                      </Text>
                    ) : null}
                    {component.paragraph.paragraph ? (
                      <Text style={styles.text}>
                        {component.paragraph.paragraph}
                      </Text>
                    ) : null}
                    {component.media.poster.url ? (
                      <View style={styles.imageContainer}>
                        <Image
                          style={styles.image}
                          src={component.media.poster.url}
                        />
                      </View>
                    ) : null}
                  </View>
                )
              }
              if (component.acf_fc_layout === 'text_on_image') {
                return (
                  <View key={index}>
                    {component.pretitle ? (
                      <Text style={styles.pretitle}>{component.pretitle}</Text>
                    ) : null}
                    {component.title ? (
                      <Text style={styles.title}>{component.title}</Text>
                    ) : null}
                    {component.text ? (
                      <Text style={styles.text}>{component.text}</Text>
                    ) : null}
                    {component.image.url ? (
                      <View style={styles.imageContainer}>
                        <Image style={styles.image} src={component.image.url} />
                      </View>
                    ) : null}
                  </View>
                )
              }
              if (component.acf_fc_layout === 'single_img') {
                return component.item.url ? (
                  <View key={index} style={styles.imageContainer}>
                    <Image style={styles.image} src={component.item.url} />
                  </View>
                ) : null
              }
              if (component.acf_fc_layout === 'simple_paragraph') {
                return (
                  <View key={index}>
                    {component.paragraph.title ? (
                      <Text style={styles.title}>
                        {component.paragraph.title}
                      </Text>
                    ) : null}
                    {component.paragraph.paragraph ? (
                      <Text style={styles.text}>
                        {component.paragraph.paragraph}
                      </Text>
                    ) : null}
                  </View>
                )
              }
              if (component.acf_fc_layout === 'double_paragraph') {
                return (
                  <View key={index}>
                    {component.paragraph_sx.pretitle ? (
                      <Text style={styles.pretitle}>
                        {component.paragraph_sx.pretitle}
                      </Text>
                    ) : null}
                    {component.paragraph_sx.title ? (
                      <Text style={styles.title}>
                        {component.paragraph_sx.title}
                      </Text>
                    ) : null}
                    {component.paragraph_sx.paragraph ? (
                      <Text style={styles.text}>
                        {component.paragraph_sx.paragraph}
                      </Text>
                    ) : null}
                    <View
                      style={{
                        height: 20,
                        backgroundColor: 'transparent',
                        marginBottom: 20
                      }}
                    />
                    {component.paragraph_dx.pretitle ? (
                      <Text style={styles.pretitle}>
                        {component.paragraph_dx.pretitle}
                      </Text>
                    ) : null}
                    {component.paragraph_dx.title ? (
                      <Text style={styles.title}>
                        {component.paragraph_dx.title}
                      </Text>
                    ) : null}
                    {component.paragraph_dx.paragraph ? (
                      <Text style={styles.text}>
                        {component.paragraph_dx.paragraph}
                      </Text>
                    ) : null}
                  </View>
                )
              }
              if (component.acf_fc_layout === 'gallery_download') {
                return (
                  <Fragment key={index}>
                    {component.gallery.length > 0 &&
                      component.gallery.map(
                        (image: { url: string; id: number }) => (
                          <View
                            key={image.id}
                            style={styles.galleryImageContainer}
                          >
                            <Image
                              key={image.id}
                              style={styles.galleryImage}
                              src={image.url}
                            />
                          </View>
                        )
                      )}
                  </Fragment>
                )
              }
            })}

        <View style={styles.footer} fixed>
          <Text
            render={({ pageNumber, totalPages }) =>
              `${pageNumber} / ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  )
}

export default ProjectDataSheet
